import { useEffect, useRef, useState } from "react";
import BandwidthHUD from "./BandwidthHUD";
import { fetchCounted, resolveChunkUrl, wireCounter } from "../lib/ipfs";
import { FakeModel } from "../codec/fake-model";
import { StreamDecoder } from "../codec/decoder";

interface ChunkEntry {
  index: number;
  /** CID for IPFS, OR url for local placeholder */
  cid?: string;
  url?: string;
  /** Length in bytes after decoding (uncompressed). Required so the AC knows when to stop. */
  decodedLen?: number;
  /** Whether this chunk is arithmetic-coded. If false, it's served raw (for the placeholder). */
  encoded: boolean;
}

interface Manifest {
  version: number;
  mime: string;
  chunkSeconds: number;
  chunks: ChunkEntry[];
}

const MANIFEST_URL = `${import.meta.env.BASE_URL}content/manifest.json`;
const RAW_VIDEO_URL = `${import.meta.env.BASE_URL}content/placeholder-video.mp4`;

export default function DemoPlayer() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [status, setStatus] = useState("idle");
  const [playbackSeconds, setPlaybackSeconds] = useState(0);
  const [decodedTotal, setDecodedTotal] = useState(0);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => setPlaybackSeconds(v.currentTime);
    v.addEventListener("timeupdate", onTime);
    return () => v.removeEventListener("timeupdate", onTime);
  }, []);

  async function startDemo() {
    const v = videoRef.current;
    if (!v) return;
    wireCounter.reset();
    setDecodedTotal(0);
    setStatus("loading manifest");

    let manifest: Manifest;
    try {
      const buf = await fetchCounted(MANIFEST_URL);
      manifest = JSON.parse(new TextDecoder().decode(buf)) as Manifest;
    } catch (e) {
      setStatus(`manifest failed: ${(e as Error).message}`);
      return;
    }

    const model = new FakeModel(0.6);
    const dec = new StreamDecoder(model);

    // Strategy:
    //   - For `encoded: true` chunks, fetch + AC-decode + assemble into a Blob.
    //   - For `encoded: false` chunks (the placeholder), fetch raw bytes directly.
    //   - Once all chunks are in, build a single Blob URL and play it. (MSE assembly
    //     is doable but the placeholder is a single mp4, so the simple path is fine.)
    setStatus("fetching chunks");
    const parts: Uint8Array[] = [];
    for (const c of manifest.chunks) {
      const url = resolveChunkUrl(c);
      const raw = await fetchCounted(url);
      if (c.encoded) {
        if (!c.decodedLen) throw new Error(`chunk ${c.index} missing decodedLen`);
        setStatus(`decoding chunk ${c.index}`);
        const decoded = await dec.decodeChunk(raw, c.decodedLen);
        parts.push(decoded);
        setDecodedTotal((t) => t + decoded.byteLength);
      } else {
        parts.push(raw);
        setDecodedTotal((t) => t + raw.byteLength);
      }
    }

    setStatus("assembling");
    const blob = new Blob(parts as BlobPart[], { type: manifest.mime });
    v.src = URL.createObjectURL(blob);
    await v.play().catch(() => {/* autoplay block is fine */});
    setStatus("playing");
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <header>
        <h1 className="text-2xl font-mono">demo</h1>
        <p className="opacity-70 text-sm mt-1">
          Pitch video streams from IPFS, decoded in-browser via arithmetic coding + a
          byte-level model. Counters show bytes-off-wire vs. the equivalent YouTube
          bitrate.
        </p>
      </header>

      <div className="aspect-video bg-white/5 rounded overflow-hidden">
        <video
          ref={videoRef}
          controls
          playsInline
          className="w-full h-full"
          poster=""
        />
      </div>

      <div className="flex items-center gap-4">
        <button
          onClick={startDemo}
          className="bg-white text-black px-4 py-2 rounded font-mono text-sm hover:bg-white/90"
        >
          start
        </button>
        <span className="font-mono text-sm opacity-70">status: {status}</span>
        {decodedTotal > 0 && (
          <span className="font-mono text-xs opacity-50">
            decoded: {(decodedTotal / 1024).toFixed(1)} KB
          </span>
        )}
      </div>

      <BandwidthHUD playbackSeconds={playbackSeconds} />

      <details className="text-xs opacity-60 font-mono">
        <summary className="cursor-pointer">debug: manifest source</summary>
        <pre className="mt-2 whitespace-pre-wrap">{MANIFEST_URL}</pre>
        <pre className="mt-1 whitespace-pre-wrap">fallback raw: {RAW_VIDEO_URL}</pre>
      </details>
    </div>
  );
}
